import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../lib/api';

export default function SubscriptionSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('loading'); // loading | paid | expired | error
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setState('error');
      return;
    }
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (res.data?.payment_status === 'paid') {
          setState('paid');
          return;
        }
        // Still pending — retry up to 15 times
        setTries(t => {
          if (t + 1 >= 15) {
            setState('expired');
            return t + 1;
          }
          setTimeout(poll, 2000);
          return t + 1;
        });
      } catch {
        if (cancelled) return;
        setState('error');
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white rounded-3xl shadow-2xl p-8 md:p-10 text-center">
        {state === 'loading' && (
          <>
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
            </div>
            <h1 className="font-heading font-black text-2xl text-slate-900 mb-2">Confirmation en cours…</h1>
            <p className="text-slate-600">Nous vérifions votre paiement auprès de Stripe.</p>
          </>
        )}
        {state === 'paid' && (
          <>
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg">
              <CheckCircle2 className="w-12 h-12 text-white" />
            </div>
            <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-3">
              <Sparkles className="w-3 h-3" /> Bienvenue !
            </span>
            <h1 className="font-heading font-black text-3xl text-slate-900 mb-2">Abonnement activé 🎉</h1>
            <p className="text-slate-600 mb-6">
              Vos frais de gestion sont désormais offerts sur les colis inclus dans votre formule. Vous pouvez commencer à envoyer dès maintenant.
            </p>
            <div className="flex flex-col gap-3">
              <Button onClick={() => navigate('/dashboard')} className="bg-blue-600 hover:bg-blue-700 h-12" data-testid="sub-success-dashboard">
                Aller à mon tableau de bord <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Link to="/abonnements" className="text-sm text-slate-500 hover:text-blue-600">Voir mon abonnement</Link>
            </div>
          </>
        )}
        {(state === 'expired' || state === 'error') && (
          <>
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="font-heading font-black text-2xl text-slate-900 mb-2">Traitement en cours</h1>
            <p className="text-slate-600 mb-6">
              Le paiement met un peu de temps à se confirmer. Vérifiez votre tableau de bord dans quelques instants ou rafraîchissez la page.
            </p>
            <Button onClick={() => navigate('/dashboard')} className="bg-blue-600 hover:bg-blue-700">
              Aller au tableau de bord
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
